/**
 * @fileoverview Shopping Cart API Service Layer
 *
 * This file contains all API service functions for shopping cart operations.
 * It provides a clean abstraction layer between the cart components and
 * the backend services.
 *
 * Core API Operations:
 * - updateBasketItemQuantity: Updates item quantities in the basket
 * - removeBasketItem: Removes items from the basket
 * - getBasketList: Fetches current cart items
 * - getCheckoutData: Retrieves checkout form data and calculations
 * - submitOrder: Processes final order placement
 *
 * Key Features:
 * - Consistent error handling across all API calls
 * - Type-safe request/response handling
 * - Proper HTTP status code management
 * - Standardized response format for component consumption
 * - Integration with global API configuration
 */

import api, { BaseAvamaeResponse } from "api";
import { endpoints } from "config";

/**
 * Updates the quantity of an item in the basket
 * @param basketId - The basket ID from shopBasket__Id
 * @param catalogueItemsId - The catalogue item ID
 * @param quantity - The new quantity
 * @returns Promise with success status and optional error message
 */
export const updateBasketItemQuantity = async (
  basketId: number,
  catalogueItemsId: number,
  quantity: number
): Promise<{
  success: boolean;
  message?: string;
}> => {
  try {
    const updateData = {
      id: basketId,
      catalogueItemsId,
      quantity,
    };

    const response = await api.post<BaseAvamaeResponse<any>>(
      endpoints.shop.basket.update,
      updateData
    );

    if (response.data.status === "1") {
      return {
        success: true,
        message: "Item quantity updated successfully.",
      };
    } else {
      console.error("Failed to update basket item:", response.data.errors);
      return {
        success: false,
        message: "There was an error updating the item quantity. Please try again.",
      };
    }
  } catch (error) {
    console.error("Error updating basket item:", error);
    return {
      success: false,
      message: "Unable to connect to the server. Please check your connection and try again.",
    };
  }
};

/**
 * Removes an item from the basket
 * @param basketId - The basket ID from shopBasket__Id
 * @returns Promise with success status and optional error message
 */
export const removeBasketItem = async (
  basketId: number
): Promise<{
  success: boolean;
  message?: string;
}> => {
  try {
    const response = await api.delete<BaseAvamaeResponse<any>>(
      `${endpoints.shop.basket.delete}?id=${basketId}`
    );

    // Handle both success status "1" and HTTP 204 (No Content) as success
    if (response.data.status === "1" || response.status === 204) {
      return {
        success: true,
        message: "Item removed from basket successfully.",
      };
    } else {
      console.error("Failed to remove basket item:", response.data?.errors);
      return {
        success: false,
        message: "There was an error removing the item. Please try again.",
      };
    }
  } catch (error: any) {
    console.error("Error removing basket item:", error);

    // Check if it's actually a successful 204 response that axios is treating as an error
    if (error.response?.status === 204) {
      return {
        success: true,
        message: "Item removed from basket successfully.",
      };
    }

    return {
      success: false,
      message: "Unable to connect to the server. Please check your connection and try again.",
    };
  }
};

/**
 * Gets the checkout data including form configuration and basket state
 * @returns Promise with checkout data or error
 */
export const getCheckoutData = async (): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  try {
    const response = await api.get<BaseAvamaeResponse<any>>(endpoints.shop.basket.checkout);

    if (response.data.status === "1") {
      return {
        success: true,
        data: response.data,
      };
    } else {
      console.error("Failed to get checkout data:", response.data.errors);
      return {
        success: false,
        message: "There was an error loading checkout data. Please try again.",
      };
    }
  } catch (error) {
    console.error("Error getting checkout data:", error);
    return {
      success: false,
      message: "Unable to connect to the server. Please check your connection and try again.",
    };
  }
};

/**
 * Submits the order with checkout payload data
 * @param payload - The formatted checkout payload
 * @returns Promise with success status and optional error message
 */
export const submitOrder = async (
  payload: any
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  try {
    const response = await api.post<BaseAvamaeResponse<any>>(
      endpoints.shop.basket.checkout,
      payload
    );

    if (response.data.status === "1") {
      console.log("Order submitted successfully:", response.data);
      return {
        success: true,
        data: response.data,
        message: "Your order has been placed successfully!",
      };
    } else {
      console.error("Failed to submit order:", response.data.errors);
      return {
        success: false,
        message: "There was an error placing your order. Please try again.",
      };
    }
  } catch (error) {
    console.error("Error submitting order:", error);
    return {
      success: false,
      message: "Unable to connect to the server. Please check your connection and try again.",
    };
  }
};
